/**
 * Shared microphone stream manager.
 * getUserMedia takes ~0.5-1s to spin up, which was cutting the start of recordings.
 * Dialogs "warm" the mic when they open so tapping record starts INSTANTLY.
 * The stream is auto-released after use (or on dialog close) to respect privacy.
 *
 * CRITICAL (iOS): before touching getUserMedia we take the GLOBAL mic lock
 * (micExclusiveAcquire) which pauses the "Hey Buddy" speech recognizer.
 * Opening/closing the mic while the iOS recognizer engine runs crashes the
 * whole app — this protects EVERY mic user (chat voice notes, Buddy,
 * dictation, video) in one place.
 */
import { Capacitor } from "@capacitor/core";
import { micExclusiveAcquire, micExclusiveRelease } from "./wakeword";

let cachedStream = null;
let releaseTimer = null;
let holdingLock = false;

export async function warmMic() {
  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
  if (cachedStream && cachedStream.active) return cachedStream;
  if (!holdingLock) {
    holdingLock = true;
    const engineWasRunning = micExclusiveAcquire();
    // give the recognizer's audio engine a moment to fully stop — grabbing
    // the mic mid-teardown crashes the app on iOS
    if (engineWasRunning && Capacitor.isNativePlatform()) {
      await new Promise((r) => setTimeout(r, 200));
    }
  }
  try {
    cachedStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    if (holdingLock) { holdingLock = false; micExclusiveRelease(); }
    throw e;
  }
  return cachedStream;
}

// Same as warmMic — returns the live stream immediately if already warm
export const getMic = warmMic;

function doRelease() {
  releaseTimer = null;
  if (cachedStream) {
    try { cachedStream.getTracks().forEach((tk) => tk.stop()); } catch (e) { /* ignore */ }
    cachedStream = null;
  }
  if (holdingLock) {
    holdingLock = false;
    micExclusiveRelease(); // wake listener resumes after a settle delay
  }
}

export function releaseMic(delayMs = 0) {
  if (releaseTimer) clearTimeout(releaseTimer);
  if (delayMs > 0) releaseTimer = setTimeout(doRelease, delayMs);
  else doRelease();
}
